import { useEffect, useState } from "react";
import validator from "validator";
import { toast } from "react-hot-toast";
import axios from "axios";
import { HiCheck, HiX } from "react-icons/hi";
import { FiUploadCloud, FiEye, FiEyeOff, FiChevronDown, FiSave, FiClock, FiTrash2 } from "react-icons/fi";
import { IoArrowBackOutline, IoArrowForwardOutline } from "react-icons/io5";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { useAddVendorMutation, useGetAllVendorThemeNamesMutation } from "../../../features/admin/vendor/vendorApi";
import { listDrafts, saveDraft, deleteDraft } from "./clientDrafts";

const STEPS = [
  { label: "Basic Info",     desc: "Credentials, identity and contact details." },
  { label: "Logos",          desc: "Upload login page and navbar logos." },
  { label: "Business Setup", desc: "Industry, compliance and panel configuration." },
  { label: "Review",         desc: "Confirm everything before registering." },
];

const inputCls =
  "block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";
const selectCls =
  "block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all appearance-none cursor-pointer";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";
const errCls   = "mt-1.5 text-xs text-red-500 flex items-center gap-1";

// ─── Select wrapper — fixes native select text clipping ───────────────────────
const Select = ({ value, onChange, children, className }) => (
  <div className="relative">
    <select value={value} onChange={onChange} className={`${selectCls} ${className || ""}`}>
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
  </div>
);

// ─── Password rules ───────────────────────────────────────────────────────────
const PW_RULES = [
  { label: "At least 8 characters",    test: (p) => p.length >= 8 },
  { label: "One uppercase letter",      test: (p) => /[A-Z]/.test(p) },
  { label: "One lowercase letter",      test: (p) => /[a-z]/.test(p) },
  { label: "One number",                test: (p) => /[0-9]/.test(p) },
  { label: "One special character",     test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const pwStrength = (p) => PW_RULES.filter(r => r.test(p)).length;

const PasswordStrengthBar = ({ password }) => {
  if (!password) return null;
  const score = pwStrength(password);
  const segments = [
    score >= 1 ? (score <= 2 ? "bg-red-400" : score <= 3 ? "bg-amber-400" : "bg-emerald-500") : "bg-gray-200",
    score >= 3 ? (score <= 3 ? "bg-amber-400" : "bg-emerald-500") : "bg-gray-200",
    score >= 5 ? "bg-emerald-500" : "bg-gray-200",
  ];
  const label = score <= 2 ? "Weak" : score <= 3 ? "Fair" : score === 4 ? "Good" : "Strong";
  const labelColor = score <= 2 ? "text-red-500" : score <= 3 ? "text-amber-500" : "text-emerald-600";
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {segments.map((cls, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${cls}`} />
        ))}
      </div>
      <p className={`text-xs mt-1 font-medium ${labelColor}`}>{label}</p>
    </div>
  );
};

const PasswordRules = ({ password }) => {
  if (!password) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-y-1 gap-x-3">
      {PW_RULES.map(r => {
        const ok = r.test(password);
        return (
          <div key={r.label} className={`flex items-center gap-1.5 text-xs transition-colors ${ok ? "text-emerald-600" : "text-gray-400"}`}>
            <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[9px] font-bold flex-shrink-0 ${ok ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300"}`}>
              {ok ? "✓" : ""}
            </span>
            {r.label}
          </div>
        );
      })}
    </div>
  );
};

// ─── Password field with show/hide ────────────────────────────────────────────
const PasswordField = ({ label, value, onChange, placeholder, showRules, confirm, original }) => {
  const [show, setShow] = useState(false);
  const matchOk = confirm && value.length > 0 && value === original;

  return (
    <div>
      <label className={labelCls}>
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          className={`${inputCls} pr-10`}
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete="new-password"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      </div>
      {showRules && <PasswordStrengthBar password={value} />}
      {showRules && <PasswordRules password={value} />}
      {confirm && value.length > 0 && (
        <p className={`mt-1.5 text-xs flex items-center gap-1 font-medium ${matchOk ? "text-emerald-600" : "text-red-500"}`}>
          {matchOk ? <HiCheck className="h-3.5 w-3.5" /> : <HiX className="h-3.5 w-3.5" />}
          {matchOk ? "Passwords match" : "Passwords do not match"}
        </p>
      )}
    </div>
  );
};

// ─── Stepper ──────────────────────────────────────────────────────────────────
const WizardStepper = ({ steps, current }) => (
  <div className="flex items-center">
    {steps.map((s, i) => {
      const done   = i < current;
      const active = i === current;
      return (
        <div key={s.label} className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all ${
              active ? "bg-blue-600 text-white ring-4 ring-blue-100"
                     : done  ? "bg-blue-600 text-white"
                              : "bg-white text-gray-400 border border-gray-300"
            }`}>
              {done ? <HiCheck className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`text-sm hidden md:block ${
              active ? "font-semibold text-gray-900"
                     : done  ? "font-medium text-gray-600"
                              : "text-gray-400"
            }`}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-3 rounded ${done ? "bg-blue-600" : "bg-gray-200"}`} />
          )}
        </div>
      );
    })}
  </div>
);

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 mt-5">
    {(title || desc) && (
      <div className="mb-6 pb-4 border-b border-gray-100">
        {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
        {desc  && <p  className="text-sm text-gray-500 mt-0.5">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

// ─── NavButtons ───────────────────────────────────────────────────────────────
const NavButtons = ({ step, totalSteps, onBack, onNext, onFinish, isLoading }) => (
  <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100">
    <button
      type="button"
      onClick={onBack}
      disabled={step === 0}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
    >
      <IoArrowBackOutline className="h-4 w-4" />
      Back
    </button>

    {step < totalSteps - 1 ? (
      <button
        type="button"
        onClick={onNext}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-sm font-semibold shadow-sm transition-all"
      >
        Continue
        <IoArrowForwardOutline className="h-4 w-4" />
      </button>
    ) : (
      <button
        type="button"
        onClick={onFinish}
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {isLoading ? (
          <><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Registering…</>
        ) : (
          <><HiCheck className="h-4 w-4" /> Register Client</>
        )}
      </button>
    )}
  </div>
);

// ─── Logo upload box ──────────────────────────────────────────────────────────
const LogoUploadBox = ({ title, badge, file, onFileChange, onUpload, isUploading, preview, error }) => (
  <div className="flex flex-col gap-3">
    <div>
      <p className="text-sm font-medium text-gray-700">
        {title} <span className="text-red-500">*</span>
      </p>
      {badge && <p className="text-xs text-gray-400 mt-0.5">{badge}</p>}
    </div>

    <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-7 cursor-pointer transition-colors text-center ${
      preview ? "border-blue-300 bg-blue-50/40" : "border-gray-200 hover:border-blue-400 hover:bg-blue-50/30"
    }`}>
      <FiUploadCloud className={`h-6 w-6 ${preview ? "text-blue-400" : "text-gray-400"}`} />
      <span className="text-xs text-gray-500 font-medium">
        {file ? file[0]?.name : "Click to choose file"}
      </span>
      <span className="text-xs text-gray-400">PNG, JPG, SVG</span>
      <input type="file" accept="image/*" className="hidden" onChange={e => onFileChange(e.target.files)} />
    </label>

    {file && !preview && (
      <button
        type="button"
        onClick={onUpload}
        disabled={isUploading}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 transition-all"
      >
        {isUploading
          ? <><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading…</>
          : <><FiUploadCloud className="h-4 w-4" /> Upload</>}
      </button>
    )}

    {preview && (
      <div className="relative group">
        <div className="flex items-center justify-center border border-blue-200 rounded-xl p-3 bg-blue-50/30 h-24">
          <img src={preview} alt={title} className="max-h-full max-w-full object-contain" />
        </div>
        <div className="flex items-center gap-1.5 mt-2">
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500">
            <HiCheck className="h-2.5 w-2.5 text-white" />
          </span>
          <span className="text-xs text-emerald-600 font-medium">Uploaded successfully</span>
        </div>
        {/* Re-upload on change */}
        {file && (
          <button
            type="button"
            onClick={onUpload}
            disabled={isUploading}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-all"
          >
            {isUploading
              ? <><span className="h-3.5 w-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Uploading…</>
              : <><FiUploadCloud className="h-3.5 w-3.5" /> Replace</>}
          </button>
        )}
      </div>
    )}

    {error && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <HiX className="h-3.5 w-3.5 shrink-0" /> {error}
      </p>
    )}
  </div>
);

// ─── Review row ───────────────────────────────────────────────────────────────
const ReviewRow = ({ label, value }) =>
  value ? (
    <div className="flex gap-3 py-2.5 border-b border-gray-100 last:border-0 text-sm">
      <span className="w-44 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium break-all">{value}</span>
    </div>
  ) : null;

const ReviewSection = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{title}</p>
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-4">{children}</div>
  </div>
);

// ==================== MAIN WIZARD ====================

const MyWizard = ({ handleClose, refetch }) => {
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    rePassword: "",
    mobile: "",
    theme_name: "",
    contact_person_name: "",
    contact_person_number: "",
    db_name: "db1",
    gstin: "",
    panel_status: "0",
    status: "1",
    website: "",
    qr_type: "1",
    industry_id: 0,
    thumbnailLogo: null,
    navbarLogo: null,
  });

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const [vendorThemeNames, setVendorThemeNames] = useState([]);
  const [industryData, setIndustryData]         = useState([]);
  const [logoType, setLogoType]                 = useState(null);
  const [thumbFile, setThumbFile]               = useState(null);
  const [navFile, setNavFile]                   = useState(null);
  // track new file chosen after a replace so upload button re-appears
  const [thumbDirty, setThumbDirty]             = useState(false);
  const [navDirty, setNavDirty]                 = useState(false);

  // ── Draft state ───────────────────────────────────────────────────────────────
  const [activeDraftId, setActiveDraftId]   = useState(null);
  const [draftName, setDraftName]           = useState("");
  const [drafts, setDrafts]                 = useState(() => listDrafts());
  const [showDrafts, setShowDrafts]         = useState(false);
  const [lastSavedAt, setLastSavedAt]       = useState(null);

  const savedLabel = lastSavedAt
    ? `Saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : null;

  const [fetchVendorThemeNames, { data: themeNamesData, error: themeNamesError }] =
    useGetAllVendorThemeNamesMutation();
  const [uploadImage, { data: uploadImageData, isLoading: uploadImageIsLoading, error: uploadImageError }] =
    useUploadSingleFileMutation();
  const [addVendor, { data: addVendorData, isLoading: addVendorIsLoading, error: addVendorError }] =
    useAddVendorMutation();

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchVendorThemeNames({ token: sessionStorage.getItem("access_token") });
    axios({
      method: "get",
      url: `${process.env.REACT_APP_BASE_URL}/admin/industry`,
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + sessionStorage.getItem("access_token") },
    }).then(r => setIndustryData(r.data.body)).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (themeNamesData?.body) setVendorThemeNames(themeNamesData.body.map(t => t.tenant_name));
    if (themeNamesError) toast.error("Could not load theme names", { id: "themeErr" });
  }, [themeNamesData, themeNamesError]);

  // Auto-generate theme_name from company name
  useEffect(() => {
    setField("theme_name", form.name.replaceAll(" ", "-").toLowerCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  // Duplicate domain warning
  useEffect(() => {
    toast.dismiss();
    if (vendorThemeNames.length && form.theme_name && vendorThemeNames.includes(form.theme_name)) {
      toast.error(`Domain "${form.theme_name}" is already taken`, { id: "dupTheme" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.theme_name]);

  useEffect(() => {
    if (uploadImageData) {
      if (logoType === 0) { setField("thumbnailLogo", uploadImageData.body.fileLink); setThumbDirty(false); }
      if (logoType === 1) { setField("navbarLogo",    uploadImageData.body.fileLink); setNavDirty(false); }
    }
    if (uploadImageError) toast.error("Image upload failed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadImageData, uploadImageError]);

  useEffect(() => {
    if (addVendorData) {
      toast.dismiss();
      toast.success("Client registered successfully!");
      if (activeDraftId) { deleteDraft(activeDraftId); setActiveDraftId(null); }
      handleClose();
      setStep(0);
      refetch(true);
    }
    if (addVendorError) {
      toast.dismiss();
      toast.error(addVendorError?.data?.message || "Error creating client");
    }
    if (addVendorIsLoading) toast.loading("Registering client…");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addVendorData, addVendorError, addVendorIsLoading]);

  // ── Validation ────────────────────────────────────────────────────────────────

  const validators = {
    0: () => {
      const e = {};
      if (!form.name.trim())                                             e.name      = "Company name is required";
      if (!validator.isEmail(form.email))                               e.email     = "Enter a valid email address";
      if (!validator.isStrongPassword(form.password, { minLength: 8, minLowercase: 1, minUppercase: 1, minNumbers: 1, minSymbols: 1 }))
        e.password = "Password does not meet the requirements below";
      if (form.rePassword !== form.password)                            e.rePassword = "Passwords do not match";
      if (!validator.isMobilePhone(form.mobile, ["en-IN"]))            e.mobile    = "Enter a valid 10-digit mobile number";
      if (vendorThemeNames.includes(form.theme_name))                  e.theme_name = `Domain "${form.theme_name}" is already taken`;
      return e;
    },
    1: () => {
      const e = {};
      if (!form.thumbnailLogo) e.thumbnailLogo = "Login page logo is required";
      if (!form.navbarLogo)    e.navbarLogo    = "Navbar logo is required";
      return e;
    },
    2: () => {
      const e = {};
      if (!form.industry_id || Number(form.industry_id) === 0) e.industry_id = "Please select an industry";
      if (!form.gstin || form.gstin.trim().length < 15)         e.gstin       = "GSTIN must be 15 characters";
      return e;
    },
    3: () => ({}),
  };

  // ── Draft helpers ─────────────────────────────────────────────────────────────

  const buildDraftData = () => ({ ...form });

  const persistDraft = (stepForDraft = step) => {
    const hasContent = form.name || form.email || form.mobile;
    if (!hasContent && !activeDraftId) return;
    const id = activeDraftId || Date.now();
    if (!activeDraftId) setActiveDraftId(id);
    const name = draftName || form.name || "Untitled draft";
    if (!draftName) setDraftName(name);
    saveDraft({ id, name, currentStep: stepForDraft, data: buildDraftData() });
    setLastSavedAt(Date.now());
    setDrafts(listDrafts());
  };

  const applyDraft = (d) => {
    const data = d.data || {};
    setForm({
      name:                  data.name                  || "",
      email:                 data.email                 || "",
      password:              data.password              || "",
      rePassword:            data.rePassword            || "",
      mobile:                data.mobile                || "",
      theme_name:            data.theme_name            || "",
      contact_person_name:   data.contact_person_name   || "",
      contact_person_number: data.contact_person_number || "",
      db_name:               data.db_name               || "db1",
      gstin:                 data.gstin                 || "",
      panel_status:          data.panel_status          || "0",
      status:                data.status                || "1",
      website:               data.website               || "",
      qr_type:               data.qr_type               || "1",
      industry_id:           data.industry_id           || 0,
      thumbnailLogo:         data.thumbnailLogo         || null,
      navbarLogo:            data.navbarLogo            || null,
    });
    setStep(d.currentStep || 0);
    setActiveDraftId(d.id);
    setDraftName(d.name || "");
    setShowDrafts(false);
    toast.success(`Draft "${d.name}" loaded`);
  };

  const removeDraft = (id) => {
    deleteDraft(id);
    setDrafts(listDrafts());
    if (id === activeDraftId) { setActiveDraftId(null); setDraftName(""); }
  };

  // ── Navigation ────────────────────────────────────────────────────────────────

  const handleNext = () => {
    const e = validators[step]?.() || {};
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    const next = step + 1;
    persistDraft(next);
    setStep(next);
  };

  const handleBack = () => { setErrors({}); persistDraft(step - 1); setStep(s => s - 1); };

  // ── Logo upload ────────────────────────────────────────────────────────────────

  const uploadLogo = (file, type) => {
    const fd = new FormData();
    fd.append("image", file[0]);
    setLogoType(type);
    uploadImage({ body: fd, token: sessionStorage.getItem("access_token") });
  };

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleFinish = () => {
    let industryName = "";
    let user_types   = [];
    industryData.forEach(d => {
      if (String(d.industry_id) === String(form.industry_id)) { industryName = d.name; user_types = d.users; }
    });

    addVendor({
      token: sessionStorage.getItem("access_token"),
      body: {
        name:                  form.name,
        email:                 form.email,
        password:              form.password,
        mobile:                form.mobile,
        theme_name:            form.theme_name,
        contact_person_name:   form.contact_person_name,
        contact_person_number: form.contact_person_number,
        db_name:               form.db_name,
        gstin:                 form.gstin,
        panel_status:          form.panel_status,
        status:                form.status,
        website:               form.website,
        qr_type:               form.qr_type,
        user_types:            JSON.stringify(user_types),
        industry_id:           form.industry_id,
        industry_name:         industryName,
        logo:                  [form.thumbnailLogo, form.navbarLogo],
      },
    });
  };

  // ── Step renderers ────────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (step) {
      // ── STEP 0 : Basic Info ──────────────────────────────────────────────────
      case 0:
        return (
          <>
            <SectionCard title="Company Identity" desc="The name and domain that will identify this client.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Company Name <span className="text-red-500">*</span></label>
                  <input
                    className={inputCls}
                    placeholder="e.g. Acme Corporation"
                    value={form.name}
                    onChange={e => setField("name", e.target.value)}
                  />
                  {errors.name && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.name}</p>}
                </div>
                <div>
                  <label className={labelCls}>Domain / Theme Name</label>
                  <input
                    className={inputCls}
                    placeholder="auto-generated from name"
                    value={form.theme_name}
                    onChange={e => setField("theme_name", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-400">Used as the client's unique slug — auto-filled from company name.</p>
                  {errors.theme_name && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.theme_name}</p>}
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Login Credentials" desc="Secure credentials the company admin will use to sign in.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Email Address <span className="text-red-500">*</span></label>
                  <input
                    className={inputCls}
                    type="email"
                    placeholder="admin@company.com"
                    value={form.email}
                    onChange={e => setField("email", e.target.value)}
                    autoComplete="off"
                  />
                  {errors.email && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.email}</p>}
                </div>
                <div>
                  <label className={labelCls}>Mobile Number <span className="text-red-500">*</span></label>
                  <input
                    className={inputCls}
                    type="tel"
                    placeholder="10-digit Indian mobile"
                    maxLength={10}
                    value={form.mobile}
                    onChange={e => setField("mobile", e.target.value)}
                  />
                  {errors.mobile && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.mobile}</p>}
                </div>
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <PasswordField
                      label="Password"
                      value={form.password}
                      onChange={e => setField("password", e.target.value)}
                      placeholder="Create a strong password"
                      showRules
                    />
                    {errors.password && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.password}</p>}
                  </div>
                  <div>
                    <PasswordField
                      label="Confirm Password"
                      value={form.rePassword}
                      onChange={e => setField("rePassword", e.target.value)}
                      placeholder="Re-enter your password"
                      confirm
                      original={form.password}
                    />
                    {errors.rePassword && !form.rePassword.length && (
                      <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.rePassword}</p>
                    )}
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Contact Details" desc="Primary contact person for this account (optional).">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className={labelCls}>Contact Person Name</label>
                  <input className={inputCls} placeholder="Full name" value={form.contact_person_name} onChange={e => setField("contact_person_name", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Contact Person Number</label>
                  <input className={inputCls} type="tel" placeholder="Mobile number" maxLength={10} value={form.contact_person_number} onChange={e => setField("contact_person_number", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Database</label>
                  <Select value={form.db_name} onChange={e => setField("db_name", e.target.value)}>
                    <option value="db1">db1</option>
                    {process.env.REACT_APP_MODE === "PROD" && <option value="db2">db2</option>}
                  </Select>
                </div>
              </div>
            </SectionCard>
          </>
        );

      // ── STEP 1 : Logos ───────────────────────────────────────────────────────
      case 1:
        return (
          <SectionCard title="Company Logos" desc="Upload both logos before proceeding. Recommended: transparent background PNG.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <LogoUploadBox
                title="Login Page Logo"
                badge="Displayed on the client's sign-in screen"
                file={thumbFile}
                onFileChange={f => { setThumbFile(f); setThumbDirty(true); }}
                onUpload={() => uploadLogo(thumbFile, 0)}
                isUploading={uploadImageIsLoading && logoType === 0}
                preview={!thumbDirty ? form.thumbnailLogo : null}
                error={errors.thumbnailLogo}
              />
              <LogoUploadBox
                title="Navbar Logo"
                badge="Displayed in the top navigation bar"
                file={navFile}
                onFileChange={f => { setNavFile(f); setNavDirty(true); }}
                onUpload={() => uploadLogo(navFile, 1)}
                isUploading={uploadImageIsLoading && logoType === 1}
                preview={!navDirty ? form.navbarLogo : null}
                error={errors.navbarLogo}
              />
            </div>
          </SectionCard>
        );

      // ── STEP 2 : Business Setup ───────────────────────────────────────────────
      case 2:
        return (
          <SectionCard title="Business Configuration" desc="Industry, compliance details and panel environment settings.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelCls}>Industry <span className="text-red-500">*</span></label>
                <Select value={form.industry_id} onChange={e => setField("industry_id", e.target.value)}>
                  <option value={0}>— Select an industry —</option>
                  {industryData.map(ind => (
                    <option key={ind.industry_id} value={ind.industry_id}>{ind.name}</option>
                  ))}
                </Select>
                {errors.industry_id && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.industry_id}</p>}
              </div>
              <div>
                <label className={labelCls}>GSTIN <span className="text-red-500">*</span></label>
                <input
                  className={inputCls}
                  maxLength={16}
                  placeholder="15-character GST number"
                  value={form.gstin}
                  onChange={e => setField("gstin", e.target.value.toUpperCase())}
                />
                {form.gstin.length > 0 && (
                  <p className={`mt-1 text-xs font-medium ${form.gstin.length === 15 ? "text-emerald-600" : "text-gray-400"}`}>
                    {form.gstin.length}/15 characters
                  </p>
                )}
                {errors.gstin && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" />{errors.gstin}</p>}
              </div>
              <div>
                <label className={labelCls}>Company Website</label>
                <input className={inputCls} placeholder="https://example.com" value={form.website} onChange={e => setField("website", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>QR Type <span className="text-red-500">*</span></label>
                <Select value={form.qr_type} onChange={e => setField("qr_type", e.target.value)}>
                  <option value="1">Standard QR Codes</option>
                  <option value="2">Enterprise QR Codes</option>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Panel Environment</label>
                <Select value={form.panel_status} onChange={e => setField("panel_status", e.target.value)}>
                  <option value="0">Demo</option>
                  <option value="1">Testing / Staging</option>
                  <option value="2">Pre-Production</option>
                  <option value="3">Production (Live)</option>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Account Status</label>
                <Select value={form.status} onChange={e => setField("status", e.target.value)}>
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </Select>
              </div>
            </div>
          </SectionCard>
        );

      // ── STEP 3 : Review ──────────────────────────────────────────────────────
      case 3: {
        const panelLabels = ["Demo", "Testing / Staging", "Pre-Production", "Production (Live)"];
        const industryName = industryData.find(i => String(i.industry_id) === String(form.industry_id))?.name || "";
        return (
          <SectionCard title="Review & Confirm" desc="Double-check all details — the company will be created immediately on submit.">
            <div className="space-y-5">
              <ReviewSection title="Identity">
                <ReviewRow label="Company Name"   value={form.name} />
                <ReviewRow label="Domain Name"    value={form.theme_name} />
                <ReviewRow label="Database"       value={form.db_name} />
              </ReviewSection>
              <ReviewSection title="Credentials">
                <ReviewRow label="Email"   value={form.email} />
                <ReviewRow label="Mobile"  value={form.mobile} />
                <ReviewRow label="Password" value={"•".repeat(form.password.length)} />
              </ReviewSection>
              {(form.contact_person_name || form.contact_person_number) && (
                <ReviewSection title="Contact">
                  <ReviewRow label="Contact Person"  value={form.contact_person_name} />
                  <ReviewRow label="Contact Number"  value={form.contact_person_number} />
                </ReviewSection>
              )}
              <ReviewSection title="Business">
                <ReviewRow label="Industry"          value={industryName} />
                <ReviewRow label="GSTIN"             value={form.gstin} />
                <ReviewRow label="QR Type"           value={form.qr_type === "1" ? "Standard QR Codes" : "Enterprise QR Codes"} />
                <ReviewRow label="Website"           value={form.website} />
                <ReviewRow label="Panel Environment" value={panelLabels[Number(form.panel_status)] || form.panel_status} />
                <ReviewRow label="Status"            value={form.status === "1" ? "Active" : "Inactive"} />
              </ReviewSection>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Logos</p>
                <div className="flex gap-5">
                  {form.thumbnailLogo && (
                    <div className="text-center">
                      <div className="flex items-center justify-center border border-gray-200 rounded-xl p-2 bg-gray-50 h-16 w-28 mb-1">
                        <img src={form.thumbnailLogo} alt="Login" className="max-h-full object-contain" />
                      </div>
                      <p className="text-xs text-gray-400">Login Page</p>
                    </div>
                  )}
                  {form.navbarLogo && (
                    <div className="text-center">
                      <div className="flex items-center justify-center border border-gray-200 rounded-xl p-2 bg-gray-50 h-16 w-28 mb-1">
                        <img src={form.navbarLogo} alt="Navbar" className="max-h-full object-contain" />
                      </div>
                      <p className="text-xs text-gray-400">Navbar</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>
        );
      }

      default: return null;
    }
  };

  return (
    <div className="relative">

      {/* ── Draft toolbar ── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {savedLabel && (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <HiCheck className="h-3.5 w-3.5" /> {savedLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => persistDraft()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FiSave className="h-3.5 w-3.5" />
            Save draft
          </button>
          <button
            type="button"
            onClick={() => { setDrafts(listDrafts()); setShowDrafts(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors"
          >
            <FiClock className="h-3.5 w-3.5" />
            Drafts
            {drafts.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/20 text-[11px] font-semibold">
                {drafts.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Stepper ── */}
      <div className="bg-blue-50 rounded-xl p-4">
        <WizardStepper steps={STEPS} current={step} />
      </div>

      {/* ── Step content ── */}
      {renderStep()}

      {/* ── Navigation ── */}
      <NavButtons
        step={step}
        totalSteps={STEPS.length}
        onBack={handleBack}
        onNext={handleNext}
        onFinish={handleFinish}
        isLoading={addVendorIsLoading}
      />

      {/* ── Drafts panel overlay ── */}
      {showDrafts && (
        <div className="absolute inset-0 z-20 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Saved Drafts</h3>
              <p className="text-xs text-gray-500 mt-0.5">Resume where you left off — drafts are stored locally.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowDrafts(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <HiX className="h-4 w-4" />
            </button>
          </div>

          {/* Draft list */}
          <div className="flex-1 overflow-auto p-5">
            {drafts.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <FiClock className="h-10 w-10 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No saved drafts yet</p>
                <p className="text-xs text-gray-400">Click "Save draft" while filling the form to save your progress.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {drafts.map((d) => (
                  <div
                    key={d.id}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3.5 transition-colors ${
                      d.id === activeDraftId
                        ? "border-blue-300 bg-blue-50/50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">{d.name || "Untitled draft"}</p>
                        {d.id === activeDraftId && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wide flex-shrink-0">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Step {(d.currentStep || 0) + 1} of {STEPS.length} · {new Date(d.updatedAt).toLocaleString()}
                      </p>
                      {d.data?.name && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{d.data.email}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => applyDraft(d)}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                      >
                        Resume
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDraft(d.id)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyWizard;
